import React, { useState, useEffect, useCallback, useRef } from 'react';
import { 
  DollarSign, TrendingUp, TrendingDown, Car, Megaphone, Shield, 
  Package, Users, Plus, Edit, Trash2, Check, X, AlertTriangle,
  Calendar, Filter, Download, RefreshCw, ChevronLeft, FileText,
  Upload, FileSpreadsheet, Lock, Unlock, Clock, Ban, CheckCircle2,
  FileDown, Mail, RotateCcw
} from 'lucide-react';
import axios from 'axios';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const EXPENSE_CATEGORIES = {
  vehicle: { name: 'Fahrzeugkosten', icon: Car, color: 'blue' },
  advertising: { name: 'Werbungskosten', icon: Megaphone, color: 'purple' },
  insurance: { name: 'Versicherungen', icon: Shield, color: 'green' },
  materials: { name: 'Material & Ausrüstung', icon: Package, color: 'orange' },
  personnel: { name: 'Personal', icon: Users, color: 'pink' },
  other: { name: 'Sonstige', icon: FileText, color: 'gray' }
};

const AccountingPanel = ({ password, onBack }) => {
  const [activeTab, setActiveTab] = useState('overview');
  const [income, setIncome] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [summary, setSummary] = useState(null);
  const [pendingConfirmations, setPendingConfirmations] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [filterMonth, setFilterMonth] = useState('');
  const [filterYear, setFilterYear] = useState(new Date().getFullYear().toString());
  
  // New states for advanced features
  const [unmatchedEntries, setUnmatchedEntries] = useState([]);
  const [monthStatus, setMonthStatus] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const fileInputRef = useRef(null);
  
  // Form states
  const [showIncomeForm, setShowIncomeForm] = useState(false);
  const [showExpenseForm, setShowExpenseForm] = useState(false);
  const [editingIncome, setEditingIncome] = useState(null);
  const [editingExpense, setEditingExpense] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState('vehicle');
  
  const [newIncome, setNewIncome] = useState({
    type: 'manual',
    description: '',
    customerName: '',
    customerEmail: '',
    date: new Date().toISOString().split('T')[0],
    preliminaryAmount: '',
    confirmedAmount: '',
    notes: ''
  });
  
  const [newExpense, setNewExpense] = useState({
    category: 'vehicle',
    description: '',
    amount: '',
    date: new Date().toISOString().split('T')[0],
    vendor: '',
    invoiceNumber: '',
    notes: ''
  });

  // Load data
  const loadData = useCallback(async () => {
    setIsLoading(true);
    try {
      const params = new URLSearchParams({ password });
      if (filterMonth) params.append('month', filterMonth);
      else if (filterYear) params.append('year', filterYear);
      
      const [incomeRes, expensesRes, summaryRes, pendingRes, unmatchedRes] = await Promise.all([
        axios.get(`${API}/accounting/income?${params}`),
        axios.get(`${API}/accounting/expenses?${params}`),
        axios.get(`${API}/accounting/summary?${params}`),
        axios.get(`${API}/accounting/pending-confirmations?password=${password}`),
        axios.get(`${API}/accounting/unmatched?password=${password}`)
      ]);
      
      setIncome(incomeRes.data);
      setExpenses(expensesRes.data);
      setSummary(summaryRes.data);
      setPendingConfirmations(pendingRes.data);
      setUnmatchedEntries(unmatchedRes.data);
      
      // Load month status if month is selected
      if (filterMonth) {
        try {
          const monthRes = await axios.get(`${API}/accounting/month-status/${filterMonth}?password=${password}`);
          setMonthStatus(monthRes.data);
        } catch (e) {
          setMonthStatus(null);
        }
      } else {
        setMonthStatus(null);
      }
    } catch (error) {
      toast.error('Fehler beim Laden der Daten');
    } finally {
      setIsLoading(false);
    }
  }, [password, filterMonth, filterYear]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Income functions
  const handleAddIncome = async () => {
    if (!newIncome.description || !newIncome.customerName || !newIncome.preliminaryAmount) {
      toast.error('Bitte füllen Sie alle Pflichtfelder aus');
      return;
    }
    
    try {
      await axios.post(`${API}/accounting/income?password=${password}`, {
        ...newIncome,
        preliminaryAmount: parseFloat(newIncome.preliminaryAmount),
        confirmedAmount: newIncome.confirmedAmount ? parseFloat(newIncome.confirmedAmount) : null
      });
      toast.success('Einnahme hinzugefügt');
      setShowIncomeForm(false);
      setNewIncome({
        type: 'manual',
        description: '',
        customerName: '',
        customerEmail: '',
        date: new Date().toISOString().split('T')[0],
        preliminaryAmount: '',
        confirmedAmount: '',
        notes: ''
      });
      loadData();
    } catch (error) {
      toast.error('Fehler beim Hinzufügen');
    }
  };

  const handleConfirmIncome = async (incomeId, amount) => {
    try {
      await axios.put(`${API}/accounting/income/${incomeId}?password=${password}`, {
        confirmedAmount: parseFloat(amount),
        status: 'confirmed'
      });
      toast.success('Betrag bestätigt');
      loadData();
    } catch (error) {
      toast.error('Fehler beim Bestätigen');
    }
  };

  const handleDeleteIncome = async (incomeId) => {
    if (!window.confirm('Eintrag wirklich löschen?')) return;
    try {
      await axios.delete(`${API}/accounting/income/${incomeId}?password=${password}`);
      toast.success('Eintrag gelöscht');
      loadData();
    } catch (error) {
      toast.error('Fehler beim Löschen');
    }
  };

  // Expense functions
  const handleAddExpense = async () => {
    if (!newExpense.description || !newExpense.amount) {
      toast.error('Bitte füllen Sie alle Pflichtfelder aus');
      return;
    }
    
    try {
      await axios.post(`${API}/accounting/expenses?password=${password}`, {
        ...newExpense,
        amount: parseFloat(newExpense.amount)
      });
      toast.success('Ausgabe hinzugefügt');
      setShowExpenseForm(false);
      setNewExpense({
        category: selectedCategory,
        description: '',
        amount: '',
        date: new Date().toISOString().split('T')[0],
        vendor: '',
        invoiceNumber: '',
        notes: ''
      });
      loadData();
    } catch (error) {
      toast.error('Fehler beim Hinzufügen');
    }
  };

  const handleDeleteExpense = async (expenseId) => {
    if (!window.confirm('Eintrag wirklich löschen?')) return;
    try {
      await axios.delete(`${API}/accounting/expenses/${expenseId}?password=${password}`);
      toast.success('Eintrag gelöscht');
      loadData();
    } catch (error) {
      toast.error('Fehler beim Löschen');
    }
  };

  // Send reminders
  const handleSendReminders = async () => {
    try {
      const response = await axios.post(`${API}/accounting/send-reminders?password=${password}`);
      if (response.data.count > 0) {
        toast.success(`Erinnerung für ${response.data.count} Einträge gesendet`);
      } else {
        toast.info('Keine Erinnerungen zu senden');
      }
    } catch (error) {
      toast.error('Fehler beim Senden der Erinnerungen');
    }
  };

  // Upload bank statement
  const handleBankStatementUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    
    if (!file.name.endsWith('.csv')) {
      toast.error('Nur CSV-Dateien werden unterstützt');
      return;
    }
    
    setIsUploading(true);
    setUploadResult(null);
    
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      const response = await axios.post(
        `${API}/accounting/upload-bank-statement?password=${password}`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      
      setUploadResult(response.data);
      toast.success(`${response.data.matched} Zahlungen automatisch zugeordnet`);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Hochladen');
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  // Assign unmatched entry to category
  const handleAssignUnmatched = async (entryId, category, description) => {
    try {
      await axios.post(
        `${API}/accounting/unmatched/${entryId}/assign?password=${password}`,
        { category, description }
      );
      toast.success('Eintrag zugeordnet');
      loadData();
    } catch (error) {
      toast.error('Fehler beim Zuordnen');
    }
  };

  // Delete unmatched entry
  const handleDeleteUnmatched = async (entryId) => {
    if (!window.confirm('Eintrag als privat markieren und löschen?')) return;
    try {
      await axios.delete(`${API}/accounting/unmatched/${entryId}?password=${password}`);
      toast.success('Eintrag gelöscht');
      loadData();
    } catch (error) {
      toast.error('Fehler beim Löschen');
    }
  };

  // Generate recurring payments
  const handleGenerateRecurringPayments = async () => {
    try {
      const response = await axios.post(`${API}/accounting/generate-recurring-payments?password=${password}`);
      if (response.data.generated?.length > 0) {
        toast.success(`${response.data.generated.length} fällige Zahlungen generiert`);
        loadData();
      } else {
        toast.info('Keine fälligen Zahlungen gefunden');
      }
    } catch (error) {
      toast.error('Fehler beim Generieren');
    }
  };

  // Close month
  const handleCloseMonth = async () => {
    if (!filterMonth) {
      toast.error('Bitte wählen Sie einen Monat aus');
      return;
    }
    
    if (!window.confirm(`Monat ${filterMonth} wirklich abschließen?\n\nDer Monat wird schreibgeschützt und ein PDF-Bericht wird generiert und per E-Mail versendet.`)) {
      return;
    }
    
    try {
      const response = await axios.post(`${API}/accounting/close-month/${filterMonth}?password=${password}`);
      toast.success(`Monat ${filterMonth} wurde abgeschlossen`);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Abschließen');
    }
  };

  // Check month reminders
  const handleCheckMonthReminders = async () => {
    try {
      const response = await axios.post(`${API}/accounting/check-month-reminders?password=${password}`);
      toast.info(response.data.message);
    } catch (error) {
      toast.error('Fehler beim Prüfen');
    }
  };

  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(amount || 0);
  };

  // Format date
  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    
    // Handle German date format (DD.MM.YYYY)
    if (dateStr.includes('.')) {
      const parts = dateStr.split('.');
      if (parts.length === 3) {
        return dateStr; // Already in German format
      }
    }
    
    // Handle ISO format (YYYY-MM-DD)
    return new Date(dateStr + 'T00:00:00').toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <div className="bg-gradient-to-r from-green-600 to-blue-600 text-white p-6">
        <div className="max-w-7xl mx-auto">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-4">
              <Button variant="ghost" onClick={onBack} className="text-white hover:bg-white/20">
                <ChevronLeft className="h-5 w-5" />
              </Button>
              <div>
                <h1 className="text-2xl font-bold flex items-center gap-2">
                  <DollarSign className="h-7 w-7" /> Buchhaltung
                </h1>
                <p className="text-green-100">Einnahmen, Ausgaben & Bilanz</p>
              </div>
            </div>
            <div className="flex gap-2">
              <Button variant="secondary" size="sm" onClick={loadData} disabled={isLoading}>
                <RefreshCw className={`h-4 w-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} /> Aktualisieren
              </Button>
              <Button variant="secondary" size="sm" onClick={handleSendReminders}>
                <AlertTriangle className="h-4 w-4 mr-2" /> Erinnerungen senden
              </Button>
            </div>
          </div>
          
          {/* Filter */}
          <div className="mt-4 flex gap-4 items-center">
            <div className="flex items-center gap-2">
              <Filter className="h-4 w-4" />
              <span className="text-sm">Filter:</span>
            </div>
            <select
              value={filterYear}
              onChange={(e) => { setFilterYear(e.target.value); setFilterMonth(''); }}
              className="px-3 py-1 rounded bg-white/20 text-white border-0 text-sm"
            >
              <option value="2026">2026</option>
              <option value="2025">2025</option>
              <option value="">Alle Jahre</option>
            </select>
            <select
              value={filterMonth}
              onChange={(e) => setFilterMonth(e.target.value)}
              className="px-3 py-1 rounded bg-white/20 text-white border-0 text-sm"
            >
              <option value="">Alle Monate</option>
              {[...Array(12)].map((_, i) => (
                <option key={i} value={`${filterYear}-${String(i + 1).padStart(2, '0')}`}>
                  {new Date(2000, i).toLocaleString('de-DE', { month: 'long' })}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto p-4 md:p-6">
        {/* Summary Cards */}
        {summary && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            <Card className="bg-gradient-to-br from-green-50 to-green-100 border-green-200">
              <CardContent className="p-4">
                <div className="flex justify-between items-start">
                  <div>
                    <p className="text-sm text-green-600 font-medium">Einnahmen (bestätigt)</p>
                    <p className="text-2xl font-bold text-green-700">{formatCurrency(summary.income?.total)}</p>
                    <p className="text-xs text-green-600">{summary.income?.count} Einträge</p>
                  </div>
                  <TrendingUp className="h-8 w-8 text-green-500" />
                </div>
              </CardContent>
            </Card>
            
            <Card className="bg-gradient-to-br from-yellow-50 to-yellow-100 border-yellow-200">
              <CardContent className="p-4">
                <div className="flex justify-between items-start">
                  <div>
                    <p className="text-sm text-yellow-600 font-medium">Unbestätigt</p>
                    <p className="text-2xl font-bold text-yellow-700">{formatCurrency(summary.pendingIncome?.total)}</p>
                    <p className="text-xs text-yellow-600">{summary.pendingIncome?.count} ausstehend</p>
                  </div>
                  <AlertTriangle className="h-8 w-8 text-yellow-500" />
                </div>
              </CardContent>
            </Card>
            
            <Card className="bg-gradient-to-br from-red-50 to-red-100 border-red-200">
              <CardContent className="p-4">
                <div className="flex justify-between items-start">
                  <div>
                    <p className="text-sm text-red-600 font-medium">Ausgaben</p>
                    <p className="text-2xl font-bold text-red-700">{formatCurrency(summary.expenses?.total)}</p>
                    <p className="text-xs text-red-600">{summary.expenses?.count} Einträge</p>
                  </div>
                  <TrendingDown className="h-8 w-8 text-red-500" />
                </div>
              </CardContent>
            </Card>
            
            <Card className={`bg-gradient-to-br ${summary.balance >= 0 ? 'from-blue-50 to-blue-100 border-blue-200' : 'from-orange-50 to-orange-100 border-orange-200'}`}>
              <CardContent className="p-4">
                <div className="flex justify-between items-start">
                  <div>
                    <p className={`text-sm font-medium ${summary.balance >= 0 ? 'text-blue-600' : 'text-orange-600'}`}>Bilanz</p>
                    <p className={`text-2xl font-bold ${summary.balance >= 0 ? 'text-blue-700' : 'text-orange-700'}`}>{formatCurrency(summary.balance)}</p>
                    <p className={`text-xs ${summary.balance >= 0 ? 'text-blue-600' : 'text-orange-600'}`}>
                      Marge: {summary.profitMargin}%
                    </p>
                  </div>
                  <DollarSign className={`h-8 w-8 ${summary.balance >= 0 ? 'text-blue-500' : 'text-orange-500'}`} />
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
          {[
            { id: 'overview', label: 'Übersicht', icon: FileText },
            { id: 'income', label: 'Einnahmen', icon: TrendingUp },
            { id: 'pending', label: 'Zu bestätigen', icon: AlertTriangle, badge: pendingConfirmations.length },
            { id: 'bankstatement', label: 'Kontoauszug', icon: FileSpreadsheet },
            { id: 'unmatched', label: 'Nicht zugeordnet', icon: Ban, badge: unmatchedEntries.length },
            { id: 'monthclose', label: 'Monatsabschluss', icon: Lock },
            { id: 'vehicle', label: 'Fahrzeug', icon: Car },
            { id: 'advertising', label: 'Werbung', icon: Megaphone },
            { id: 'insurance', label: 'Versicherung', icon: Shield },
            { id: 'materials', label: 'Material', icon: Package },
            { id: 'personnel', label: 'Personal', icon: Users }
          ].map(tab => (
            <Button
              key={tab.id}
              variant={activeTab === tab.id ? 'default' : 'outline'}
              size="sm"
              onClick={() => { setActiveTab(tab.id); if (['vehicle', 'advertising', 'insurance', 'materials', 'personnel'].includes(tab.id)) setSelectedCategory(tab.id); }}
              className="whitespace-nowrap"
              data-testid={`tab-${tab.id}`}
            >
              <tab.icon className="h-4 w-4 mr-2" />
              {tab.label}
              {tab.badge > 0 && (
                <span className="ml-2 bg-red-500 text-white text-xs px-2 py-0.5 rounded-full">{tab.badge}</span>
              )}
            </Button>
          ))}
        </div>

        {/* Pending Confirmations */}
        {activeTab === 'pending' && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center text-yellow-600">
                <AlertTriangle className="h-5 w-5 mr-2" /> Zu bestätigen ({pendingConfirmations.length})
              </CardTitle>
              <CardDescription>Einnahmen, die nach Abschluss der Fahrt/Zahlung bestätigt werden müssen</CardDescription>
            </CardHeader>
            <CardContent>
              {pendingConfirmations.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <Check className="h-12 w-12 mx-auto mb-3 text-green-500" />
                  <p>Alle Einnahmen sind bestätigt!</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {pendingConfirmations.map(entry => (
                    <PendingConfirmationCard
                      key={entry.id}
                      entry={entry}
                      onConfirm={handleConfirmIncome}
                      formatCurrency={formatCurrency}
                      formatDate={formatDate}
                    />
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Income Tab */}
        {activeTab === 'income' && (
          <Card>
            <CardHeader>
              <div className="flex justify-between items-center">
                <div>
                  <CardTitle className="flex items-center text-green-600">
                    <TrendingUp className="h-5 w-5 mr-2" /> Einnahmen
                  </CardTitle>
                  <CardDescription>Alle Einnahmen aus Buchungen und Flatrates</CardDescription>
                </div>
                <Button onClick={() => setShowIncomeForm(true)} className="bg-green-600 hover:bg-green-700">
                  <Plus className="h-4 w-4 mr-2" /> Manuelle Fahrt
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {/* Income Form */}
              {showIncomeForm && (
                <div className="mb-6 p-4 bg-green-50 rounded-lg border border-green-200">
                  <h4 className="font-semibold mb-4">Neue Fahrt hinzufügen</h4>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <Label>Beschreibung *</Label>
                      <Input
                        value={newIncome.description}
                        onChange={(e) => setNewIncome({ ...newIncome, description: e.target.value })}
                        placeholder="z.B. Transport zum Tierarzt"
                      />
                    </div>
                    <div>
                      <Label>Kundenname *</Label>
                      <Input
                        value={newIncome.customerName}
                        onChange={(e) => setNewIncome({ ...newIncome, customerName: e.target.value })}
                        placeholder="Max Mustermann"
                      />
                    </div>
                    <div>
                      <Label>E-Mail</Label>
                      <Input
                        value={newIncome.customerEmail}
                        onChange={(e) => setNewIncome({ ...newIncome, customerEmail: e.target.value })}
                        placeholder="kunde@email.de"
                      />
                    </div>
                    <div>
                      <Label>Datum *</Label>
                      <Input
                        type="date"
                        value={newIncome.date}
                        onChange={(e) => setNewIncome({ ...newIncome, date: e.target.value })}
                      />
                    </div>
                    <div>
                      <Label>Betrag (vorläufig) *</Label>
                      <Input
                        type="number"
                        step="0.01"
                        value={newIncome.preliminaryAmount}
                        onChange={(e) => setNewIncome({ ...newIncome, preliminaryAmount: e.target.value })}
                        placeholder="0.00"
                      />
                    </div>
                    <div>
                      <Label>Betrag (bestätigt)</Label>
                      <Input
                        type="number"
                        step="0.01"
                        value={newIncome.confirmedAmount}
                        onChange={(e) => setNewIncome({ ...newIncome, confirmedAmount: e.target.value })}
                        placeholder="Leer = unbestätigt"
                      />
                    </div>
                    <div className="md:col-span-3">
                      <Label>Notizen</Label>
                      <Input
                        value={newIncome.notes}
                        onChange={(e) => setNewIncome({ ...newIncome, notes: e.target.value })}
                        placeholder="Zusätzliche Informationen..."
                      />
                    </div>
                  </div>
                  <div className="flex gap-2 mt-4">
                    <Button onClick={handleAddIncome} className="bg-green-600 hover:bg-green-700">
                      <Check className="h-4 w-4 mr-2" /> Speichern
                    </Button>
                    <Button variant="outline" onClick={() => setShowIncomeForm(false)}>
                      <X className="h-4 w-4 mr-2" /> Abbrechen
                    </Button>
                  </div>
                </div>
              )}

              {/* Income Table */}
              <IncomeTable
                income={income}
                onDelete={handleDeleteIncome}
                onConfirm={handleConfirmIncome}
                formatCurrency={formatCurrency}
                formatDate={formatDate}
              />
            </CardContent>
          </Card>
        )}

        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Income by Type */}
            <Card>
              <CardHeader>
                <CardTitle className="text-green-600">Einnahmen nach Typ</CardTitle>
              </CardHeader>
              <CardContent>
                {summary?.income?.byType && Object.entries(summary.income.byType).map(([type, amount]) => (
                  <div key={type} className="flex justify-between py-2 border-b last:border-0">
                    <span className="capitalize">{type === 'booking' ? '🚗 Fahrten' : type === 'flatrate' ? '🐾 Flatrate' : '📝 Manuell'}</span>
                    <span className="font-semibold text-green-600">{formatCurrency(amount)}</span>
                  </div>
                ))}
                <div className="flex justify-between py-2 mt-2 border-t-2 font-bold">
                  <span>Gesamt</span>
                  <span className="text-green-700">{formatCurrency(summary?.income?.total)}</span>
                </div>
              </CardContent>
            </Card>

            {/* Expenses by Category */}
            <Card>
              <CardHeader>
                <CardTitle className="text-red-600">Ausgaben nach Kategorie</CardTitle>
              </CardHeader>
              <CardContent>
                {summary?.expenses?.byCategory && Object.entries(summary.expenses.byCategory).map(([cat, amount]) => {
                  const catInfo = EXPENSE_CATEGORIES[cat] || EXPENSE_CATEGORIES.other;
                  return (
                    <div key={cat} className="flex justify-between py-2 border-b last:border-0">
                      <span className="flex items-center gap-2">
                        <catInfo.icon className="h-4 w-4" />
                        {catInfo.name}
                      </span>
                      <span className="font-semibold text-red-600">{formatCurrency(amount)}</span>
                    </div>
                  );
                })}
                <div className="flex justify-between py-2 mt-2 border-t-2 font-bold">
                  <span>Gesamt</span>
                  <span className="text-red-700">{formatCurrency(summary?.expenses?.total)}</span>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Bank Statement Upload Tab */}
        {activeTab === 'bankstatement' && (
          <Card>
            <CardHeader className="bg-gradient-to-r from-indigo-50 to-purple-50">
              <div className="flex justify-between items-center">
                <div>
                  <CardTitle className="flex items-center text-indigo-600">
                    <FileSpreadsheet className="h-5 w-5 mr-2" /> Kontoauszug hochladen
                  </CardTitle>
                  <CardDescription>Laden Sie einen CSV-Export Ihres Kontoauszugs hoch, um Zahlungen automatisch zuzuordnen</CardDescription>
                </div>
                <Button onClick={handleGenerateRecurringPayments} variant="outline">
                  <RotateCcw className="h-4 w-4 mr-2" /> Fällige Zahlungen generieren
                </Button>
              </div>
            </CardHeader>
            <CardContent className="p-6">
              <div className="space-y-6">
                {/* Upload Area */}
                <div className="border-2 border-dashed border-indigo-200 rounded-lg p-8 text-center bg-indigo-50/50">
                  <Upload className="h-12 w-12 mx-auto text-indigo-400 mb-4" />
                  <h3 className="text-lg font-semibold text-indigo-700 mb-2">CSV-Datei hochladen</h3>
                  <p className="text-sm text-gray-600 mb-4">
                    Unterstützte Formate: Sparkasse, Volksbank, und andere deutsche Banken<br/>
                    (Spalten: Buchungstag, Verwendungszweck, Betrag)
                  </p>
                  <input
                    type="file"
                    ref={fileInputRef}
                    accept=".csv"
                    onChange={handleBankStatementUpload}
                    className="hidden"
                    data-testid="bank-statement-upload"
                  />
                  <Button 
                    onClick={() => fileInputRef.current?.click()} 
                    disabled={isUploading}
                    className="bg-indigo-600 hover:bg-indigo-700"
                  >
                    {isUploading ? (
                      <><RefreshCw className="h-4 w-4 mr-2 animate-spin" /> Wird verarbeitet...</>
                    ) : (
                      <><Upload className="h-4 w-4 mr-2" /> CSV-Datei auswählen</>
                    )}
                  </Button>
                </div>

                {/* Upload Result */}
                {uploadResult && (
                  <div className="bg-white rounded-lg border p-6">
                    <h4 className="font-semibold text-lg mb-4 flex items-center">
                      <CheckCircle2 className="h-5 w-5 text-green-500 mr-2" />
                      Verarbeitung abgeschlossen
                    </h4>
                    <div className="grid grid-cols-3 gap-4 mb-6">
                      <div className="bg-green-50 p-4 rounded-lg text-center">
                        <p className="text-2xl font-bold text-green-600">{uploadResult.matched}</p>
                        <p className="text-sm text-green-700">Automatisch zugeordnet</p>
                      </div>
                      <div className="bg-red-50 p-4 rounded-lg text-center">
                        <p className="text-2xl font-bold text-red-600">{uploadResult.newExpenses}</p>
                        <p className="text-sm text-red-700">Neue Ausgaben</p>
                      </div>
                      <div className="bg-yellow-50 p-4 rounded-lg text-center">
                        <p className="text-2xl font-bold text-yellow-600">{uploadResult.unmatched}</p>
                        <p className="text-sm text-yellow-700">Manuell zuordnen</p>
                      </div>
                    </div>
                    
                    {uploadResult.details?.length > 0 && (
                      <div className="max-h-64 overflow-y-auto">
                        <table className="w-full text-sm">
                          <thead className="bg-gray-50 sticky top-0">
                            <tr>
                              <th className="p-2 text-left">Datum</th>
                              <th className="p-2 text-left">Beschreibung</th>
                              <th className="p-2 text-right">Betrag</th>
                              <th className="p-2 text-left">Status</th>
                            </tr>
                          </thead>
                          <tbody>
                            {uploadResult.details.map((detail, idx) => (
                              <tr key={idx} className="border-b">
                                <td className="p-2">{detail.date}</td>
                                <td className="p-2">{detail.description}</td>
                                <td className={`p-2 text-right ${detail.type === 'credit' ? 'text-green-600' : 'text-red-600'}`}>
                                  {detail.type === 'credit' ? '+' : '-'}{formatCurrency(detail.amount)}
                                </td>
                                <td className="p-2">
                                  <span className={`px-2 py-1 rounded text-xs ${
                                    detail.status === 'matched' ? 'bg-green-100 text-green-700' :
                                    detail.status === 'categorized' ? 'bg-blue-100 text-blue-700' :
                                    'bg-yellow-100 text-yellow-700'
                                  }`}>
                                    {detail.action || detail.status}
                                  </span>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                )}

                {/* Info Box */}
                <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                  <h4 className="font-semibold text-blue-700 mb-2">💡 So funktioniert der automatische Abgleich:</h4>
                  <ul className="text-sm text-blue-700 space-y-1">
                    <li>• <strong>Eingehende Zahlungen:</strong> Werden anhand der Mitgliedsnummer im Verwendungszweck zugeordnet</li>
                    <li>• <strong>Ausgaben:</strong> Werden anhand von Schlüsselwörtern automatisch kategorisiert (Tank, Werkstatt, etc.)</li>
                    <li>• <strong>Nicht erkannte Einträge:</strong> Werden im Tab &quot;Nicht zugeordnet&quot; zur manuellen Bearbeitung gespeichert</li>
                  </ul>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Unmatched Entries Tab */}
        {activeTab === 'unmatched' && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center text-orange-600">
                <Ban className="h-5 w-5 mr-2" /> Nicht zugeordnete Einträge ({unmatchedEntries.length})
              </CardTitle>
              <CardDescription>Einträge aus Kontoauszügen, die nicht automatisch zugeordnet werden konnten</CardDescription>
            </CardHeader>
            <CardContent>
              {unmatchedEntries.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <CheckCircle2 className="h-12 w-12 mx-auto mb-3 text-green-500" />
                  <p>Alle Einträge sind zugeordnet!</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {unmatchedEntries.map(entry => (
                    <UnmatchedEntryCard
                      key={entry.id}
                      entry={entry}
                      onAssign={handleAssignUnmatched}
                      onDelete={handleDeleteUnmatched}
                      formatCurrency={formatCurrency}
                      formatDate={formatDate}
                    />
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Month Close Tab */}
        {activeTab === 'monthclose' && (
          <Card>
            <CardHeader className="bg-gradient-to-r from-slate-50 to-gray-100">
              <CardTitle className="flex items-center text-slate-700">
                <Lock className="h-5 w-5 mr-2" /> Monatsabschluss
              </CardTitle>
              <CardDescription>Schließen Sie einen Monat ab und generieren Sie einen steuerberater-konformen PDF-Bericht</CardDescription>
            </CardHeader>
            <CardContent className="p-6">
              <div className="space-y-6">
                {/* Month Selection */}
                <div className="flex items-center gap-4">
                  <Label className="text-base font-semibold">Monat auswählen:</Label>
                  <select
                    value={filterMonth}
                    onChange={(e) => setFilterMonth(e.target.value)}
                    className="px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                    data-testid="month-select"
                  >
                    <option value="">-- Monat wählen --</option>
                    {[...Array(12)].map((_, i) => {
                      const month = `${filterYear}-${String(i + 1).padStart(2, '0')}`;
                      return (
                        <option key={month} value={month}>
                          {new Date(2000, i).toLocaleString('de-DE', { month: 'long' })} {filterYear}
                        </option>
                      );
                    })}
                  </select>
                </div>

                {/* Month Status */}
                {filterMonth && monthStatus && (
                  <div className={`rounded-lg border-2 p-6 ${monthStatus.isLocked ? 'bg-gray-50 border-gray-300' : 'bg-white border-blue-200'}`}>
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-lg font-semibold flex items-center">
                        {monthStatus.isLocked ? (
                          <><Lock className="h-5 w-5 text-gray-500 mr-2" /> Monat ist abgeschlossen</>
                        ) : (
                          <><Unlock className="h-5 w-5 text-blue-500 mr-2" /> Monat ist offen</>
                        )}
                      </h3>
                      {monthStatus.isLocked && (
                        <span className="bg-gray-200 text-gray-600 px-3 py-1 rounded-full text-sm">
                          Schreibgeschützt
                        </span>
                      )}
                    </div>

                    {/* Statistics */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                      <div className="bg-green-50 p-4 rounded-lg">
                        <p className="text-sm text-green-600">Einnahmen</p>
                        <p className="text-xl font-bold text-green-700">
                          {formatCurrency(monthStatus.statistics?.totalIncome || monthStatus.closure?.totalIncome || 0)}
                        </p>
                        <p className="text-xs text-green-600">{monthStatus.statistics?.incomeCount || monthStatus.closure?.incomeCount || 0} Einträge</p>
                      </div>
                      <div className="bg-red-50 p-4 rounded-lg">
                        <p className="text-sm text-red-600">Ausgaben</p>
                        <p className="text-xl font-bold text-red-700">
                          {formatCurrency(monthStatus.statistics?.totalExpenses || monthStatus.closure?.totalExpenses || 0)}
                        </p>
                        <p className="text-xs text-red-600">{monthStatus.statistics?.expenseCount || monthStatus.closure?.expenseCount || 0} Einträge</p>
                      </div>
                      <div className={`p-4 rounded-lg ${(monthStatus.statistics?.balance || monthStatus.closure?.balance || 0) >= 0 ? 'bg-blue-50' : 'bg-orange-50'}`}>
                        <p className={`text-sm ${(monthStatus.statistics?.balance || monthStatus.closure?.balance || 0) >= 0 ? 'text-blue-600' : 'text-orange-600'}`}>Ergebnis</p>
                        <p className={`text-xl font-bold ${(monthStatus.statistics?.balance || monthStatus.closure?.balance || 0) >= 0 ? 'text-blue-700' : 'text-orange-700'}`}>
                          {formatCurrency(monthStatus.statistics?.balance || monthStatus.closure?.balance || 0)}
                        </p>
                      </div>
                      {!monthStatus.isLocked && (
                        <div className="bg-yellow-50 p-4 rounded-lg">
                          <p className="text-sm text-yellow-600">Ausstehend</p>
                          <p className="text-xl font-bold text-yellow-700">
                            {monthStatus.statistics?.pendingIncome || 0}
                          </p>
                          <p className="text-xs text-yellow-600">{formatCurrency(monthStatus.statistics?.pendingAmount || 0)}</p>
                        </div>
                      )}
                    </div>

                    {/* Actions */}
                    {!monthStatus.isLocked && (
                      <div className="flex gap-4">
                        {monthStatus.statistics?.pendingIncome > 0 ? (
                          <div className="flex-1 bg-yellow-100 p-4 rounded-lg">
                            <p className="text-yellow-800 font-semibold">
                              ⚠️ Es gibt noch {monthStatus.statistics.pendingIncome} unbestätigte Einnahmen.
                            </p>
                            <p className="text-sm text-yellow-700">
                              Bitte bestätigen Sie alle Einnahmen, bevor Sie den Monat abschließen.
                            </p>
                            <Button 
                              onClick={() => setActiveTab('pending')} 
                              className="mt-3 bg-yellow-600 hover:bg-yellow-700"
                            >
                              Zu den ausstehenden Einträgen
                            </Button>
                          </div>
                        ) : (
                          <Button 
                            onClick={handleCloseMonth} 
                            className="bg-slate-700 hover:bg-slate-800"
                            data-testid="close-month-btn"
                          >
                            <Lock className="h-4 w-4 mr-2" /> Monat abschließen & PDF generieren
                          </Button>
                        )}
                      </div>
                    )}

                    {/* Closed Month Info */}
                    {monthStatus.isLocked && monthStatus.closure && (
                      <div className="bg-gray-100 p-4 rounded-lg mt-4">
                        <p className="text-sm text-gray-600">
                          <strong>Abgeschlossen am:</strong> {formatDate(monthStatus.closure.closedAt?.split('T')[0])}
                        </p>
                        {monthStatus.closure.pdfGenerated && (
                          <p className="text-sm text-gray-600">
                            <strong>PDF versendet:</strong> {monthStatus.closure.pdfSentAt ? formatDate(monthStatus.closure.pdfSentAt.split('T')[0]) : 'Ja'}
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {!filterMonth && (
                  <div className="text-center py-8 text-gray-500">
                    <Calendar className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                    <p>Bitte wählen Sie einen Monat aus, um dessen Status zu sehen.</p>
                  </div>
                )}

                {/* Actions */}
                <div className="flex gap-4">
                  <Button onClick={handleCheckMonthReminders} variant="outline">
                    <Mail className="h-4 w-4 mr-2" /> Monats-Erinnerungen prüfen
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Expense Categories */}
        {['vehicle', 'advertising', 'insurance', 'materials', 'personnel'].includes(activeTab) && (
          <ExpenseCategoryPanel
            category={activeTab}
            categoryInfo={EXPENSE_CATEGORIES[activeTab]}
            expenses={expenses.filter(e => e.category === activeTab)}
            password={password}
            onAdd={handleAddExpense}
            onDelete={handleDeleteExpense}
            showForm={showExpenseForm}
            setShowForm={setShowExpenseForm}
            newExpense={newExpense}
            setNewExpense={setNewExpense}
            formatCurrency={formatCurrency}
            formatDate={formatDate}
            onRefresh={loadData}
          />
        )}
      </div>
    </div>
  );
};

// Sub-components

const PendingConfirmationCard = ({ entry, onConfirm, formatCurrency, formatDate }) => {
  const [confirmedAmount, setConfirmedAmount] = useState(entry.preliminaryAmount);
  
  return (
    <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
      <div className="flex justify-between items-start">
        <div>
          <p className="font-semibold">{entry.description}</p>
          <p className="text-sm text-gray-600">{entry.customerName} • {formatDate(entry.date)}</p>
          {entry.notes && <p className="text-xs text-gray-500 mt-1">{entry.notes}</p>}
        </div>
        <div className="text-right">
          <p className="text-sm text-gray-500">Vorläufig:</p>
          <p className="font-semibold">{formatCurrency(entry.preliminaryAmount)}</p>
        </div>
      </div>
      <div className="mt-4 flex items-center gap-3">
        <div className="flex-1">
          <Input
            type="number"
            step="0.01"
            value={confirmedAmount}
            onChange={(e) => setConfirmedAmount(e.target.value)}
            className="text-right"
          />
        </div>
        <Button
          size="sm"
          onClick={() => onConfirm(entry.id, confirmedAmount)}
          className="bg-green-600 hover:bg-green-700"
        >
          <Check className="h-4 w-4 mr-2" /> Bestätigen
        </Button>
      </div>
    </div>
  );
};

const UnmatchedEntryCard = ({ entry, onAssign, onDelete, formatCurrency, formatDate }) => {
  const [selectedCategory, setSelectedCategory] = useState(entry.type === 'income' ? 'income' : 'vehicle');
  const [description, setDescription] = useState('');
  
  const categories = [
    { value: 'income', label: 'Als Einnahme speichern', color: 'green' },
    { value: 'vehicle', label: 'Fahrzeugkosten', color: 'blue' },
    { value: 'advertising', label: 'Werbungskosten', color: 'purple' },
    { value: 'insurance', label: 'Versicherungen', color: 'green' },
    { value: 'materials', label: 'Material & Ausrüstung', color: 'orange' },
    { value: 'personnel', label: 'Personal', color: 'pink' },
    { value: 'other', label: 'Sonstige Ausgaben', color: 'gray' }
  ];
  
  return (
    <div className={`p-4 rounded-lg border-2 ${entry.type === 'income' ? 'bg-green-50 border-green-200' : 'bg-orange-50 border-orange-200'}`}>
      <div className="flex justify-between items-start mb-3">
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-1">
            <span className={`px-2 py-0.5 rounded text-xs ${entry.type === 'income' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
              {entry.type === 'income' ? '↓ Eingang' : '↑ Ausgang'}
            </span>
            <span className="text-sm text-gray-500">{formatDate(entry.date)}</span>
          </div>
          <p className="font-semibold text-gray-800">{entry.description}</p>
        </div>
        <div className="text-right">
          <p className={`text-lg font-bold ${entry.type === 'income' ? 'text-green-600' : 'text-red-600'}`}>
            {entry.type === 'income' ? '+' : '-'}{formatCurrency(entry.amount)}
          </p>
        </div>
      </div>
      
      <div className="flex flex-wrap items-center gap-3 mt-4 pt-3 border-t border-gray-200">
        <div className="flex-1 min-w-[200px]">
          <select
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(e.target.value)}
            className="w-full px-3 py-2 rounded border focus:ring-2 focus:ring-blue-500 text-sm"
          >
            {categories
              .filter(c => entry.type === 'income' ? c.value === 'income' : c.value !== 'income')
              .map(cat => (
                <option key={cat.value} value={cat.value}>{cat.label}</option>
              ))
            }
          </select>
        </div>
        <div className="flex-1 min-w-[200px]">
          <Input
            placeholder="Beschreibung (optional)"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            className="text-sm"
          />
        </div>
        <div className="flex gap-2">
          <Button
            size="sm"
            onClick={() => onAssign(entry.id, selectedCategory, description)}
            className="bg-blue-600 hover:bg-blue-700"
          >
            <Check className="h-4 w-4 mr-1" /> Zuordnen
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={() => onDelete(entry.id)}
            className="text-red-600 hover:bg-red-50"
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  );
};

const IncomeTable = ({ income, onDelete, onConfirm, formatCurrency, formatDate }) => {
  if (income.length === 0) {
    return (
      <div className="text-center py-8 text-gray-500">
        <FileText className="h-12 w-12 mx-auto mb-3 text-gray-300" />
        <p>Keine Einnahmen vorhanden</p>
      </div>
    );
  }
  
  return (
    <div className="overflow-x-auto">
      <table className="w-full">
        <thead>
          <tr className="bg-gray-50">
            <th className="text-left p-3 text-sm font-semibold">Datum</th>
            <th className="text-left p-3 text-sm font-semibold">Typ</th>
            <th className="text-left p-3 text-sm font-semibold">Beschreibung</th>
            <th className="text-left p-3 text-sm font-semibold">Kunde</th>
            <th className="text-right p-3 text-sm font-semibold">Vorläufig</th>
            <th className="text-right p-3 text-sm font-semibold">Bestätigt</th>
            <th className="text-center p-3 text-sm font-semibold">Status</th>
            <th className="text-center p-3 text-sm font-semibold">Aktionen</th>
          </tr>
        </thead>
        <tbody>
          {income.map(entry => (
            <tr key={entry.id} className="border-b hover:bg-gray-50">
              <td className="p-3 text-sm">{formatDate(entry.date)}</td>
              <td className="p-3 text-sm">
                <span className={`px-2 py-1 rounded text-xs ${
                  entry.type === 'booking' ? 'bg-blue-100 text-blue-700' :
                  entry.type === 'flatrate' ? 'bg-purple-100 text-purple-700' :
                  'bg-gray-100 text-gray-700'
                }`}>
                  {entry.type === 'booking' ? '🚗 Fahrt' : entry.type === 'flatrate' ? '🐾 Flatrate' : '📝 Manuell'}
                </span>
              </td>
              <td className="p-3 text-sm">{entry.description}</td>
              <td className="p-3 text-sm">{entry.customerName}</td>
              <td className="p-3 text-sm text-right">{formatCurrency(entry.preliminaryAmount)}</td>
              <td className="p-3 text-sm text-right font-semibold text-green-600">
                {entry.confirmedAmount ? formatCurrency(entry.confirmedAmount) : '-'}
              </td>
              <td className="p-3 text-sm text-center">
                {entry.status === 'confirmed' ? (
                  <span className="text-green-600"><Check className="h-4 w-4 inline" /></span>
                ) : (
                  <span className="text-yellow-600"><AlertTriangle className="h-4 w-4 inline" /></span>
                )}
              </td>
              <td className="p-3 text-sm text-center">
                <Button variant="ghost" size="sm" onClick={() => onDelete(entry.id)} className="text-red-600 hover:text-red-800">
                  <Trash2 className="h-4 w-4" />
                </Button>
              </td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr className="bg-green-50 font-bold">
            <td colSpan="4" className="p-3 text-right">Summe (bestätigt):</td>
            <td className="p-3 text-right">{formatCurrency(income.reduce((sum, e) => sum + (e.preliminaryAmount || 0), 0))}</td>
            <td className="p-3 text-right text-green-700">{formatCurrency(income.reduce((sum, e) => sum + (e.confirmedAmount || 0), 0))}</td>
            <td colSpan="2"></td>
          </tr>
        </tfoot>
      </table>
    </div>
  );
};

const ExpenseCategoryPanel = ({ category, categoryInfo, expenses, password, onAdd, onDelete, showForm, setShowForm, newExpense, setNewExpense, formatCurrency, formatDate, onRefresh }) => {
  const Icon = categoryInfo.icon;
  const totalExpenses = expenses.reduce((sum, e) => sum + (e.amount || 0), 0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [receiptFile, setReceiptFile] = useState(null);
  const [uploadingReceipt, setUploadingReceipt] = useState(false);
  const receiptInputRef = useRef(null);
  
  const handleSubmit = async () => {
    if (!newExpense.description || !newExpense.amount) {
      toast.error('Bitte Beschreibung und Betrag ausfüllen');
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      // Erst Ausgabe erstellen
      const response = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/accounting/expenses?password=${password}`, {
        ...newExpense,
        category: category,
        amount: parseFloat(newExpense.amount)
      });
      
      const expenseId = response.data?.id;
      
      // Wenn Quittung vorhanden, hochladen
      if (receiptFile && expenseId) {
        setUploadingReceipt(true);
        const formData = new FormData();
        formData.append('file', receiptFile);
        
        try {
          await axios.post(
            `${process.env.REACT_APP_BACKEND_URL}/api/accounting/expenses/${expenseId}/receipt?password=${password}`,
            formData,
            { headers: { 'Content-Type': 'multipart/form-data' } }
          );
          toast.success('Ausgabe mit Quittung gespeichert');
        } catch (uploadError) {
          console.error('Error uploading receipt:', uploadError);
          toast.warning('Ausgabe gespeichert, aber Quittung konnte nicht hochgeladen werden');
        }
        setUploadingReceipt(false);
      } else {
        toast.success('Ausgabe hinzugefügt');
      }
      
      setShowForm(false);
      setReceiptFile(null);
      setNewExpense({
        category: category,
        description: '',
        amount: '',
        date: new Date().toISOString().split('T')[0],
        vendor: '',
        invoiceNumber: '',
        notes: ''
      });
      
      // Daten neu laden ohne Seite neu zu laden
      if (onRefresh) {
        onRefresh();
      }
    } catch (error) {
      console.error('Error adding expense:', error);
      toast.error('Fehler beim Hinzufügen der Ausgabe');
    } finally {
      setIsSubmitting(false);
    }
  };
  
  const handleReceiptSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      // Prüfen ob Bild oder PDF
      if (!file.type.startsWith('image/') && file.type !== 'application/pdf') {
        toast.error('Nur Bilder (JPG, PNG) oder PDF-Dateien erlaubt');
        return;
      }
      // Max 10MB
      if (file.size > 10 * 1024 * 1024) {
        toast.error('Datei zu groß (max. 10 MB)');
        return;
      }
      setReceiptFile(file);
    }
  };
  
  const handleViewReceipt = async (expenseId) => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/accounting/expenses/${expenseId}/receipt?password=${password}`,
        { responseType: 'blob' }
      );
      const url = window.URL.createObjectURL(response.data);
      window.open(url, '_blank');
    } catch (error) {
      toast.error('Quittung konnte nicht geladen werden');
    }
  };
  
  return (
    <Card>
      <CardHeader className={`bg-gradient-to-r from-${categoryInfo.color}-50 to-${categoryInfo.color}-100`}>
        <div className="flex justify-between items-center">
          <div>
            <CardTitle className="flex items-center">
              <Icon className={`h-5 w-5 mr-2 text-${categoryInfo.color}-600`} />
              {categoryInfo.name}
            </CardTitle>
            <CardDescription>Gesamt: {formatCurrency(totalExpenses)}</CardDescription>
          </div>
          <Button onClick={() => setShowForm(true)} className="bg-red-500 hover:bg-red-600">
            <Plus className="h-4 w-4 mr-2" /> Ausgabe hinzufügen
          </Button>
        </div>
      </CardHeader>
      <CardContent className="p-6">
        {/* Add Form */}
        {showForm && (
          <div className="mb-6 p-4 bg-red-50 rounded-lg border border-red-200">
            <h4 className="font-semibold mb-4">Neue Ausgabe</h4>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <Label>Beschreibung *</Label>
                <Input
                  value={newExpense.description}
                  onChange={(e) => setNewExpense({ ...newExpense, description: e.target.value })}
                  placeholder="z.B. Tankfüllung"
                />
              </div>
              <div>
                <Label>Betrag *</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={newExpense.amount}
                  onChange={(e) => setNewExpense({ ...newExpense, amount: e.target.value })}
                  placeholder="0.00"
                />
              </div>
              <div>
                <Label>Datum</Label>
                <Input
                  type="date"
                  value={newExpense.date}
                  onChange={(e) => setNewExpense({ ...newExpense, date: e.target.value })}
                />
              </div>
              <div>
                <Label>Anbieter/Lieferant</Label>
                <Input
                  value={newExpense.vendor}
                  onChange={(e) => setNewExpense({ ...newExpense, vendor: e.target.value })}
                  placeholder="z.B. Shell, ADAC"
                />
              </div>
              <div>
                <Label>Rechnungsnummer</Label>
                <Input
                  value={newExpense.invoiceNumber}
                  onChange={(e) => setNewExpense({ ...newExpense, invoiceNumber: e.target.value })}
                  placeholder="Optional"
                />
              </div>
              <div>
                <Label>Notizen</Label>
                <Input
                  value={newExpense.notes}
                  onChange={(e) => setNewExpense({ ...newExpense, notes: e.target.value })}
                  placeholder="Zusätzliche Infos"
                />
              </div>
              
              {/* Quittungs-Upload */}
              <div className="md:col-span-3">
                <Label>Quittung/Beleg (Foto oder PDF)</Label>
                <div className="mt-2 flex items-center gap-4">
                  <input
                    type="file"
                    ref={receiptInputRef}
                    accept="image/*,application/pdf"
                    onChange={handleReceiptSelect}
                    className="hidden"
                  />
                  <Button 
                    type="button" 
                    variant="outline" 
                    onClick={() => receiptInputRef.current?.click()}
                    className="flex items-center"
                  >
                    <Upload className="h-4 w-4 mr-2" />
                    {receiptFile ? 'Andere Datei wählen' : 'Quittung hochladen'}
                  </Button>
                  {receiptFile && (
                    <div className="flex items-center gap-2 text-sm text-green-600 bg-green-50 px-3 py-1 rounded">
                      <FileText className="h-4 w-4" />
                      {receiptFile.name}
                      <button 
                        onClick={() => setReceiptFile(null)} 
                        className="text-red-500 hover:text-red-700 ml-2"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  )}
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  Unterstützte Formate: JPG, PNG, PDF (max. 10 MB)
                </p>
              </div>
            </div>
            <div className="flex gap-2 mt-4">
              <Button 
                onClick={handleSubmit} 
                className="bg-red-500 hover:bg-red-600"
                disabled={isSubmitting || uploadingReceipt}
              >
                {isSubmitting ? (
                  <><RefreshCw className="h-4 w-4 mr-2 animate-spin" /> Speichern...</>
                ) : (
                  <><Check className="h-4 w-4 mr-2" /> Speichern</>
                )}
              </Button>
              <Button variant="outline" onClick={() => { setShowForm(false); setReceiptFile(null); }}>
                <X className="h-4 w-4 mr-2" /> Abbrechen
              </Button>
            </div>
          </div>
        )}

        {/* Expenses Table */}
        {expenses.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <Icon className="h-12 w-12 mx-auto mb-3 text-gray-300" />
            <p>Keine Einträge in dieser Kategorie</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-gray-50">
                  <th className="text-left p-3 text-sm font-semibold">Datum</th>
                  <th className="text-left p-3 text-sm font-semibold">Beschreibung</th>
                  <th className="text-left p-3 text-sm font-semibold">Anbieter</th>
                  <th className="text-left p-3 text-sm font-semibold">Rechnung</th>
                  <th className="text-center p-3 text-sm font-semibold">Beleg</th>
                  <th className="text-right p-3 text-sm font-semibold">Betrag</th>
                  <th className="text-center p-3 text-sm font-semibold">Aktionen</th>
                </tr>
              </thead>
              <tbody>
                {expenses.map(expense => (
                  <tr key={expense.id} className="border-b hover:bg-gray-50">
                    <td className="p-3 text-sm">{formatDate(expense.date)}</td>
                    <td className="p-3 text-sm">{expense.description}</td>
                    <td className="p-3 text-sm">{expense.vendor || '-'}</td>
                    <td className="p-3 text-sm">{expense.invoiceNumber || '-'}</td>
                    <td className="p-3 text-sm text-center">
                      {expense.hasReceipt ? (
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          onClick={() => handleViewReceipt(expense.id)}
                          className="text-blue-600 hover:text-blue-800"
                        >
                          <FileText className="h-4 w-4" />
                        </Button>
                      ) : (
                        <span className="text-gray-300">-</span>
                      )}
                    </td>
                    <td className="p-3 text-sm text-right font-semibold text-red-600">{formatCurrency(expense.amount)}</td>
                    <td className="p-3 text-sm text-center">
                      <Button variant="ghost" size="sm" onClick={() => onDelete(expense.id)} className="text-red-600 hover:text-red-800">
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="bg-red-50 font-bold">
                  <td colSpan="5" className="p-3 text-right">Summe:</td>
                  <td className="p-3 text-right text-red-700">{formatCurrency(totalExpenses)}</td>
                  <td></td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default AccountingPanel;
